import type { AssociatedJob, ProjectMonthlyData, PurchaseOrderItem } from 'api/types';
import AWS from 'aws-sdk';
import config from 'config/index';
import { format, parse } from 'date-fns';

export function dateFormatter(date: string, param?: string) {
  if (!date) return '';

  const dateValue = date.split('T');
  const isStr = /^[A-Za-z]/.test(dateValue[0]);

  if (!param && dateValue[1]) {
    return formatTime(dateValue[1]);
  }

  try {
    const dateObj = isStr ? new Date(date) : parse(dateValue[0], 'yyyy-MM-dd', new Date());

    switch (param) {
      case 'date': {
        return format(dateObj, 'MM/dd/yyyy');
      }
      case 'datetime': {
        return format(dateObj, 'MM/dd/yyyy HH:mm:ss');
      }
      default: {
        return dateValue[0] || date;
      }
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return date;
  }
}

export function removeDuplicatesFromArray<T>(arr: T[], key: keyof T): T[] {
  const uniqueArr = Array.from(new Set(arr.map((item) => JSON.stringify(item)))).map((item) =>
    JSON.parse(item)
  );
  const uniqueArrByKey = Array.from(new Map(uniqueArr.map((item) => [item[key], item])).values());

  return uniqueArrByKey;
}

export function removeNullOrEmptyValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const filteredObj = Object.fromEntries(
    Object.entries(obj).filter(([, val]) => val !== null && val !== undefined && val !== '')
  );

  return filteredObj as Partial<T>;
}

export function isValidPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber) return false;

  const cleanNumber = phoneNumber.replace(/[\s\-()+ ]/g, '');

  // Check if it's a valid length (7-15 digits for international numbers)
  if (cleanNumber.length < 7 || cleanNumber.length > 15) {
    return false;
  }

  // Country-specific validation
  if (cleanNumber.startsWith('1') && cleanNumber.length === 11) {
    // US: 1 + 10 digits
    const isValid = /^1[1-9]\d{2}[2-9]\d{6}$/.test(cleanNumber);
    return isValid;
  } else if (cleanNumber.startsWith('44') && cleanNumber.length >= 12 && cleanNumber.length <= 13) {
    // UK: 44 + 10-11 digits
    return /^44[1-9]\d{8,9}$/.test(cleanNumber);
  } else if (cleanNumber.startsWith('49') && cleanNumber.length >= 12 && cleanNumber.length <= 14) {
    // Germany: 49 + 10-12 digits
    return /^49[1-9]\d{9,11}$/.test(cleanNumber);
  }

  return false;
}

export const getValidations = () => ({
  messages: {
    required: 'This is required'
  },
  validators: {
    phoneNumber: {
      message: 'Invalid phone number',
      rule: (val) => isValidPhoneNumber(val),
      required: true
    }
  }
});

export function validatePhoneNumber(phoneNumber: string) {
  return isValidPhoneNumber(phoneNumber);
}

export function tabTitle(newTitle: string) {
  return (document.title = newTitle + ' - oneXerp');
}

export function sortArrByKey<T>(array: T[], key: string): T[] {
  return array.sort((a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0));
}

export function getPrice(param, val?: string): number {
  let total = 0;

  param?.paymentschedules?.forEach((item) => {
    const amount = Number(item.amount) || 0;

    if (val === 'total' || val === 'remaining') {
      if (item.status === 'Paid') {
        total += amount;
      }
    } else if (item.status === 'Approved for Payment') {
      total += amount;
    }
  });

  if (val === 'remaining') {
    total = (Number(param.total_price) || 0) - total;
  }

  return parseFloat(total.toFixed(2));
}

export const calculateItemsTotal = (items: PurchaseOrderItem[] = []): number =>
  parseFloat(
    items
      .reduce((total, item) => {
        const price = safeParseFloat(item.price) || 0;
        const quantity = safeParseFloat(item.quantity?.toString()) || 0;
        return total + price * quantity;
      }, 0)
      .toFixed(2)
  );

export const calculateModifiersTotal = (modifiers) =>
  safeParseFloat(modifiers?.shipping_cost || 0) +
  safeParseFloat(modifiers?.estimated_taxes || 0) -
  safeParseFloat(modifiers?.discount || 0) -
  safeParseFloat(modifiers?.deposit || 0) -
  safeParseFloat(modifiers?.refund || 0);

export const getS3Path = (fileName: string, module: string) => {
  const currentDate = new Date();
  const yyyy = currentDate.getFullYear().toString();
  let mm = (currentDate.getMonth() + 1).toString();
  if (mm.length == 1) {
    mm = `0${mm}`;
  }
  let dd = currentDate.getDate().toString();
  if (dd.length == 1) {
    dd = `0${dd}`;
  }

  return `${module}/${yyyy}/${mm}/${dd}/${fileName}`;
};

export const currencyFormat = (amount: number | string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(amount) ?? 0
  );

export const formatTime = (timeString: string) => {
  let time = '';
  if (timeString) {
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = minutes.toString().padStart(2, '0');
    time = `${formattedHours}:${formattedMinutes} ${period}`;
  }
  return time;
};

export const ALPHA_NUMERIC_DASH_REGEX = new RegExp('^[A-Za-z0-9 ]*$');

export const MAX_10_DIGITS_ONE_DECIMAL_REGEX = /^\d{0,10}(\.\d{0,1})?$/;

export const MAX_11_DIGITS_TWO_DECIMALS_REGEX = /^\d{0,11}(\.\d{0,2})?$/;

export const PHONE_VALIDATION_REGEX = /^\d{0,10}$/;

export const TAX_VALIDATION_REGEX = /^(\d{2}-\d{7})$/;

export const HOURS_VALIDATION_REGEX =
  /^$|^\.?$|^([0-9]|1[0-9]|2[0-3])(\.\d{0,1})?$|^24(\.0)?$|^\.\d{0,1}$/;

export const INTEGER_VALIDATION_REGEX = /^[0-9]+$/;

export const PERCENTAGE_VALIDATION_REGEX = /^(100(\.00?)?|(\d{1,2}(\.\d{1,2})?)?)$/;

export const DECIMAL_VALIDATION_TWO_PLACES_REGEX = /^[0-9]+(\.[0-9]{1,2})?$/;

export const WEBSITE_VALIDATION_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?(\/.*)?$/;

export const EMAIL_VALIDATION_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const JOB_NAME_VALIDATION_REGEX = /^[A-Za-z0-9 \-()']*$/;

export const levenshteinDistance = (a: string, b: string): number => {
  let tmp;
  let i, j;
  const alen = a.length;
  const blen = b.length;
  const dist: number[][] = [];

  if (alen === 0) return blen;
  if (blen === 0) return alen;

  for (i = 0; i <= alen; i++) dist[i] = [i];
  for (j = 0; j <= blen; j++) dist[0][j] = j;

  for (i = 1; i <= alen; i++) {
    for (j = 1; j <= blen; j++) {
      tmp = a[i - 1] === b[j - 1] ? 0 : 1;
      dist[i][j] = Math.min(dist[i - 1][j] + 1, dist[i][j - 1] + 1, dist[i - 1][j - 1] + tmp);
    }
  }

  return dist[alen][blen];
};

export const roundToTwoDecimalPlaces = (amount: string | number): string => {
  const numericAmount = Number(amount);
  return numericAmount.toFixed(2);
};

export const safeParseFloat = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

export function getAssociatedJobs(param: AssociatedJob[]) {
  const jobIds: string[] = [];
  param.forEach((item: AssociatedJob) => jobIds.push(item.project_code));
  return jobIds.join(', ');
}

export function parseDateToDesiredTimezone(date) {
  const options = { timeZone: 'America/Chicago' };
  return date.toLocaleString('en-US', options);
}

export const getWeekStartAndEnd = (date: Date) => {
  const currentDay = date.getDay();
  const sundayOffset = currentDay;
  const saturdayOffset = currentDay - 6;
  const startOfWeek = new Date(date);
  const endOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() - sundayOffset);
  endOfWeek.setDate(date.getDate() - saturdayOffset);
  return { startOfWeek, endOfWeek };
};

export const getPdf = (param) => {
  AWS.config.update({
    accessKeyId: localStorage.getItem('accessKeyId') ?? '',
    secretAccessKey: localStorage.getItem('secretAccessKey') ?? '',
    sessionToken: localStorage.getItem('sessionToken') ?? '',
    region: config.awsRegion
  });
  const s3 = new AWS.S3();
  const params = {
    Bucket: config.awsS3AssetsBucketName,
    Key: param.s3_uri ?? ''
  };

  s3.getObject(params, (err, data) => {
    if (err) {
      console.error(err);
    } else if (data.Body) {
      const pdfBlob = new Blob([data.Body as BlobPart], {
        type: 'application/pdf'
      });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank')?.focus();
    } else {
      console.error('No data.Body found');
    }
  });
};

export const getMaxCurrentWeekDate = () => {
  const now = new Date();

  const cstOptions: Intl.DateTimeFormatOptions = {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  };

  const cstDateParts = new Intl.DateTimeFormat('en-US', cstOptions).formatToParts(now);

  const cstYear = parseInt(cstDateParts.find((part) => part.type === 'year')!.value);
  const cstMonth = parseInt(cstDateParts.find((part) => part.type === 'month')!.value) - 1;
  const cstDay = parseInt(cstDateParts.find((part) => part.type === 'day')!.value);

  const cstDate = new Date(cstYear, cstMonth, cstDay);

  const dayOfWeek = cstDate.getDay(); // 0 is Sunday, 6 is Saturday
  const daysUntilSaturday = dayOfWeek === 6 ? 0 : 6 - dayOfWeek;

  // Set to end of current week (Saturday)
  const endOfWeek = new Date(cstDate);
  endOfWeek.setDate(cstDate.getDate() + daysUntilSaturday);

  // Format as YYYY-MM-DD
  return endOfWeek.toISOString().split('T')[0];
};

// Calculate labor total for all months combined
export const calculateLaborTotal = (data: ProjectMonthlyData[]): string => {
  const categories = ['fabrication', 'pm', 'design', 'paint', 'media', 'install'] as const;

  const total = data.reduce((sum, month) => {
    const monthTotal = categories.reduce((catSum, category) => {
      const hoursKey = `${category}_hours` as keyof ProjectMonthlyData;
      const rateKey = `${category}_rate` as keyof ProjectMonthlyData;

      const hours = Number(month[hoursKey]) || 0;
      const rate = Number(month[rateKey]) || 0;

      return catSum + hours * rate;
    }, 0);

    return sum + monthTotal;
  }, 0);

  return total.toFixed(2);
};

export const calculateMaterialsBudget = (data: ProjectMonthlyData[]): string => {
  const materials = data.reduce((sum, month) => sum + (Number(month.materials_budget) || 0), 0);
  return materials.toFixed(2);
};

// Total Budget (materials + labor)
export const calculateTotalBudget = (data: ProjectMonthlyData[]): string => {
  const materials = Number(calculateMaterialsBudget(data)) || 0;
  const labor = Number(calculateLaborTotal(data)) || 0;
  return (materials + labor).toFixed(2);
};

// Total Profit (sell price − total budget)
export const calculateTotalProfit = (data: ProjectMonthlyData[]): string => {
  const sellPrice = data.reduce((sum, month) => sum + (Number(month.sell_price) || 0), 0);
  const totalBudget = Number(calculateTotalBudget(data)) || 0;
  return (sellPrice - totalBudget).toFixed(2);
};

// Profit Margin (%)
export const calculateProfitMargin = (data: ProjectMonthlyData[]): string => {
  const sellPrice = data.reduce((sum, month) => sum + (Number(month.sell_price) || 0), 0);
  const totalBudget = Number(calculateTotalBudget(data)) || 0;

  if (sellPrice === 0) return '0.00';

  const profitMargin = ((sellPrice - totalBudget) / sellPrice) * 100;
  return profitMargin.toFixed(2);
};

// Find Maximum Completion Percentage
export const findMaxPercentage = (data: ProjectMonthlyData[]): number => {
  if (!data || data.length === 0) {
    return 0;
  }

  const maxPercentage = Math.max(
    ...data.map((month) => Number(month.projected_completion_percentage) || 0)
  );

  return maxPercentage;
};

// Generic function to calculate total for any numeric field in ProjectMonthlyData
export const calculateTotal = (
  data: ProjectMonthlyData[],
  key: keyof ProjectMonthlyData
): string => {
  if (!Array.isArray(data) || data.length === 0) return '0.00';

  const total = data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  return total.toFixed(2);
};

export const textTruncate = (text, maxLength = 50, suffix = '...') => {
  if (typeof text !== 'string') return '';
  return text.length > maxLength ? text.slice(0, maxLength) + suffix : text;
};

// Helper function to parse date string in YYYY-MM-DD format as CST date
export const parseYYYYMMDDAsCST = (dateString: string): Date => {
  // Parse as local date (YYYY-MM-DD format)
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day, 0, 0, 0, 0);

  // Convert to CST
  const cstString = date.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  const cstDate = new Date(cstString);
  cstDate.setHours(0, 0, 0, 0);

  return cstDate;
};

// Helper function to get current time in CST
export const getCurrentCST = (): Date => {
  const now = new Date();
  const cstString = now.toLocaleString('en-US', { timeZone: 'America/Chicago' });
  return new Date(cstString);
};

// Helper function to get the start of current week (Sunday) in CST
export const getCurrentWeekStart = (): Date => {
  const now = getCurrentCST();
  const dayOfWeek = now.getDay();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - dayOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);
  return startOfWeek;
};

// Helper function to get the start of previous week (Sunday) in CST
export const getPreviousWeekStart = (): Date => {
  const currentWeekStart = getCurrentWeekStart();
  const previousWeekStart = new Date(currentWeekStart);
  previousWeekStart.setDate(currentWeekStart.getDate() - 7);
  return previousWeekStart;
};

// Helper function to check if current time is past Monday 11:59 PM CST
export const isPastMondayDeadline = (): boolean => {
  const nowCST = getCurrentCST();
  const dayOfWeek = nowCST.getDay();
  const hours = nowCST.getHours();
  const minutes = nowCST.getMinutes();

  // Sunday: not past deadline
  if (dayOfWeek === 0) {
    return false;
  }

  // Monday: check if past 11:59 PM
  if (dayOfWeek === 1) {
    return hours > 23 || (hours === 23 && minutes > 59);
  }

  // Tuesday or later: past deadline
  return true;
};

export const validateDateForNonAdmin = (selectedDate: Date | string): boolean => {
  let selectedDateCST: Date;

  // Handle string dates (from date picker) vs Date objects
  if (typeof selectedDate === 'string') {
    // If it's YYYY-MM-DD format, parse carefully
    if (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      selectedDateCST = parseYYYYMMDDAsCST(selectedDate);
    } else {
      // Other format, convert normally
      const tempDate = new Date(selectedDate);
      const cstString = tempDate.toLocaleString('en-US', { timeZone: 'America/Chicago' });
      selectedDateCST = new Date(cstString);
      selectedDateCST.setHours(0, 0, 0, 0);
    }
  } else {
    // Date object
    const cstString = selectedDate.toLocaleString('en-US', { timeZone: 'America/Chicago' });
    selectedDateCST = new Date(cstString);
    selectedDateCST.setHours(0, 0, 0, 0);
  }

  const currentWeekStart = getCurrentWeekStart();
  const previousWeekStart = getPreviousWeekStart();
  const nowCST = getCurrentCST();

  console.log('=== Date Validation Debug ===');
  console.log('Input:', selectedDate);
  console.log('Selected Date CST:', selectedDateCST.toString());
  console.log('Current Week Start:', currentWeekStart.toString());
  console.log('Previous Week Start:', previousWeekStart.toString());
  console.log('Now CST:', nowCST.toString());
  console.log('Day of week now:', nowCST.getDay());
  console.log('Is past Monday deadline:', isPastMondayDeadline());
  console.log('===========================');

  // Check if date is in current week
  const isCurrentWeek = selectedDateCST >= currentWeekStart;
  console.log('Is current week:', isCurrentWeek);

  // Check if date is in previous week
  const isPreviousWeek = selectedDateCST >= previousWeekStart && selectedDateCST < currentWeekStart;
  console.log('Is previous week:', isPreviousWeek);

  // If it's current week, always allow
  if (isCurrentWeek) {
    return true;
  }

  // If it's previous week, check if we're still within the Monday deadline
  if (isPreviousWeek) {
    const allowed = !isPastMondayDeadline();
    console.log('Previous week - allowed:', allowed);
    return allowed;
  }

  // Any other date is not allowed
  console.log('Date is too old');
  return false;
};
